import React, { useReducer, lazy, useCallback, useMemo, Suspense, useEffect, useState, useRef } from 'react'
import { NoticeLoading } from '@fs/zion-icon'
import { usePageView, trackEvent } from '@fs/zion-analytics'
import {
  useOverlay,
  LayoutBand,
  useContainerWidth,
  Bleed,
  Separator,
  InfoSheet,
  useInfoSheet,
  MarginBox,
} from '@fs/zion-ui'
import { VisuallyHidden } from '@reach/visually-hidden'
import { useUser } from '@fs/zion-user'
import { getSessionId } from '@fs/zion-session'
import { useTranslation } from 'react-i18next'
import { useLocation, useHistory } from '@fs/zion-router'
import { useTopicContext } from '../../providers/TopicProvider'
import { useFamilyGroupContext } from '../../providers/FamilyGroupProvider'
import { getInvitedActivityGroups, addImageLookupUrlsToInvitations } from '../../helpers/family_helper'
import { getURLParams } from '../../helpers/url_helper'
import { StoryBanner } from '../../components/TopBanner'
import { useUserDetailsContext } from '../../providers/UserDetailsProvider'
import DiscoveryCodeCard from '../../components/DiscoveryCodeCard/DiscoveryCodeCard'
import useDiscoveryCodeCard from '../../hooks/useDiscoveryCodeCard'
import { stateReducer } from './storyReducer'
import useStoryData, { useSetInitialFilterGroup, useFilterSheetPersonChangeResponse } from './hooks'
import FlyToFeedAnimation from '../../components/FlyToFeedAnimation'

import DelayedRender from '../../components/DelayedRender'

const FamilyGroupMemberList = React.memo(lazy(() => import('./FamilyGroupMemberList')))
const ActivitygroupFilterSheet = React.memo(lazy(() => import('./ActivityGroupFilterSheet')))
const NoAnswersBox = React.memo(lazy(() => import('./NoAnswersBox')))
const FilterRow = React.memo(lazy(() => import('./FilterRow')))
const ViewInvitationsOverlay = React.memo(
  lazy(() => import('../../components/ViewInvitationsOverlay/ViewInvitationsOverlay'))
)
const InvitationsIndicatorsList = React.memo(
  lazy(() => import('../../components/InvitationIndicatorList/InvitationsIndicatorsList'))
)
const ActivityGroupsGrid = React.memo(lazy(() => import('../../components/ActivityGroupsGrid')))
const CompletedActivityGroupsList = React.memo(lazy(() => import('../../components/CompletedActivityGroupsList')))
const MemoizedStoryBanner = React.memo(StoryBanner)

const Story = () => {
  const infoSheetConfig = useInfoSheet()
  infoSheetConfig.size = 'lg'
  const overlay = useOverlay()
  const { t } = useTranslation()
  const initData = useMemo(() => ({ page_detail: 'My Story' }), [])
  usePageView(initData)
  const user = useUser()
  const { points } = useUserDetailsContext()
  const userReady = user?.signedIn && !user?.userLoading
  const location = useLocation()
  const history = useHistory()

  // Read fly-to-feed animation data from location state, then clear it so
  // pressing the browser back button doesn't re-trigger the points animation.
  const flyToFeedDataRef = useRef(location?.state?.flyToFeedData)
  const flyToFeedData = flyToFeedDataRef.current
  const [showFlyToFeed, setShowFlyToFeed] = useState(!!flyToFeedData)
  useEffect(() => {
    if (flyToFeedData) {
      history.replace({ ...location, state: { ...location.state, flyToFeedData: undefined } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // DEV ONLY: Test animation with Ctrl+Shift+F
  const [devFlyToFeedData, setDevFlyToFeedData] = useState(null)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'F') {
        // Simulate card position similar to Share page
        const cardWidth = 300
        const cardHeight = 200
        setDevFlyToFeedData({
          startPoints: points - 50,
          endPoints: points,
          isSharedToGroup: true,
          headerImage: 'https://picsum.photos/400/300',
          imageWidth: 400,
          imageHeight: 300,
          title: 'Test Activity Title',
          firstAnswerBody: 'This is a test answer body for the fly to feed animation.',
          cardPosition: {
            top: 150,
            left: (window.innerWidth - cardWidth) / 2,
            width: cardWidth,
            height: cardHeight,
          },
        })
        setShowFlyToFeed(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [points])

  const activeFlyToFeedData = devFlyToFeedData || flyToFeedData

  const handleFlyToFeedComplete = useCallback(() => {
    setShowFlyToFeed(false)
    setDevFlyToFeedData(null)
  }, [])

  const completed = useMemo(() => getURLParams(window?.location && window?.location?.search)?.completed ?? false, [])
  const initialState = useMemo(
    () => ({
      storyPage: { activePersonId: completed },
      filterGroupItem: null,
      id: 1,
    }),
    [completed]
  )

  const [{ storyPage, filterGroupItem }, dispatch] = useReducer(stateReducer, initialState)
  const { code: discoveryCode, onHide: onHideDiscoveryCode } = useDiscoveryCodeCard({
    enabled: !storyPage?.activePersonId,
  })
  const { familyGroups, invitations, refetchInvitations, familyGroup, setSharedAnswers } = useFamilyGroupContext()

  const {
    filteredAnswers,
    filteringGroups,
    allAnswers,
    isAnswersDataReady,
    selectedFilteringIndex,
    personsWithUserAtFront,
    selectedPerson,
    showFilterRowNameLabel,
    activityGroupAnswerCountData,
  } = useStoryData({
    userReady,
    user,
    filterGroupItem,
    familyGroups,
    activePersonId: storyPage?.activePersonId,
  })

  useEffect(() => {
    if (isAnswersDataReady) setSharedAnswers(filteredAnswers)
  }, [isAnswersDataReady, filteredAnswers])

  const atWidth = useContainerWidth()
  const isSmallScreen = atWidth({ default: true, md: false })
  const { categories, topicId } = useTopicContext()
  const invitationsWithAvatarsAdded = useMemo(
    () =>
      addImageLookupUrlsToInvitations({
        familyGroups,
        invitations,
        sessionId: getSessionId(),
      }),
    [familyGroups, invitations]
  )
  const invitedActivityGroups = useMemo(
    () => getInvitedActivityGroups({ categories, invitations: invitationsWithAvatarsAdded }),
    [categories, invitationsWithAvatarsAdded]
  )

  useSetInitialFilterGroup({ filteringGroups, filterGroupItem, dispatch })
  useFilterSheetPersonChangeResponse(infoSheetConfig, storyPage?.activePersonId)

  const handleActivePersonIdChange = useCallback(
    (valueToDispatch) => {
      dispatch({
        actionType: 'UPDATE_STORY_PAGE_STATE',
        value: valueToDispatch,
      })
    },
    [dispatch]
  )

  const handlePersonClick = useCallback(
    (person) => {
      infoSheetConfig.close()
      dispatch({
        actionType: 'UPDATE_STORY_PAGE_STATE',
        value: person?.memberCisId,
      })
      dispatch({
        actionType: 'UPDATE_SELECTED_FILTER',
        filterGroupItem: filteringGroups[0],
      })
    },
    [dispatch, filteringGroups, infoSheetConfig]
  )

  const handleFilterSelect = useCallback(
    (id) => {
      const filteringGroupToUse = filteringGroups?.find((testFilteringGroup) => {
        return id === testFilteringGroup?.id
      })
      infoSheetConfig.close()
      dispatch({
        actionType: 'UPDATE_SELECTED_FILTER',
        filterGroupItem: filteringGroupToUse,
      })

      trackEvent({ event_name: `StoryFilterSheet:FILTER_ITEM-${filteringGroupToUse?.label}:CLICK` })
    },
    [dispatch, filteringGroups, infoSheetConfig]
  )

  return (
    <>
      {showFlyToFeed && activeFlyToFeedData && (
        <FlyToFeedAnimation {...activeFlyToFeedData} onAnimationComplete={handleFlyToFeedComplete} />
      )}
      <VisuallyHidden>
        <h1>{t('nav.stories', 'Stories')}</h1>
      </VisuallyHidden>
      <LayoutBand>
        <Bleed sides>
          <MemoizedStoryBanner
            onActivePersonIdChange={handleActivePersonIdChange}
            activePersonId={storyPage?.activePersonId}
            points={points}
            user={user}
            isSmallScreen={isSmallScreen}
          />
        </Bleed>
        <MarginBox marginY="md">
          {!storyPage?.activePersonId && discoveryCode && (
            <DiscoveryCodeCard code={discoveryCode} onHide={onHideDiscoveryCode} />
          )}

          {invitationsWithAvatarsAdded?.length > 0 && !storyPage?.activePersonId && (
            <>
              <Separator />
              <Suspense fallback={<NoticeLoading />}>
                <InvitationsIndicatorsList overlay={overlay} invitations={invitationsWithAvatarsAdded} />
              </Suspense>
              <Suspense fallback={null}>
                <ViewInvitationsOverlay
                  overlay={overlay}
                  topicId={topicId}
                  invitations={invitationsWithAvatarsAdded}
                  invitedActivityGroups={invitedActivityGroups}
                  refetchInvitations={refetchInvitations}
                />
              </Suspense>
            </>
          )}

          {storyPage?.activePersonId && showFilterRowNameLabel && (
            <Suspense fallback={<NoticeLoading />}>
              <FamilyGroupMemberList
                selectedPersonCisId={selectedPerson?.memberCisId}
                unreadAnswers={allAnswers?.filter((item) => item.unread)}
                groupMembers={personsWithUserAtFront}
                onClick={handlePersonClick}
              />
            </Suspense>
          )}
          {!storyPage?.activePersonId && (
            <DelayedRender frames={2}>
              <Suspense fallback={<NoticeLoading />}>
                <ActivityGroupsGrid
                  categories={categories}
                  topicId={topicId}
                  answerCounts={activityGroupAnswerCountData}
                />
              </Suspense>
            </DelayedRender>
          )}

          {storyPage?.activePersonId && (
            <>
              <Separator />
              <Suspense fallback={<NoticeLoading />}>
                <FilterRow
                  showLabel={showFilterRowNameLabel}
                  filterButtonDisabled={!filteredAnswers?.length > 0}
                  personName={selectedPerson?.contactName ?? selectedPerson?.userProfileName}
                  filterLabel={filterGroupItem?.label}
                  onFilterClick={infoSheetConfig.handleClick}
                />
              </Suspense>
              <Separator />

              {isAnswersDataReady && selectedPerson?.memberCisId === user?.cisId && !filteredAnswers && (
                <Suspense fallback={<NoticeLoading />}>
                  <NoAnswersBox
                    iconUrl={filterGroupItem?.iconSvgUrl}
                    title="No stories here...yet!"
                    description={t('story.no-answers', `You have not completed any activities.*`)}
                  />
                </Suspense>
              )}
              {isAnswersDataReady && selectedPerson?.memberCisId !== user?.cisId && !filteredAnswers?.length > 0 && (
                <Suspense fallback={<NoticeLoading />}>
                  <NoAnswersBox
                    iconUrl={filterGroupItem?.iconSvgUrl}
                    title="No stories here...yet!"
                    description={t(
                      'story.no-shared-answers',
                      'This person has not shared any completed activities with you.*'
                    )}
                  />
                </Suspense>
              )}

              <Suspense fallback={<NoticeLoading />}>
                <CompletedActivityGroupsList
                  answers={filteredAnswers}
                  selectedPersonCisId={selectedPerson?.memberCisId}
                  topicId={topicId}
                  familyGroup={familyGroup}
                  allPersons={personsWithUserAtFront}
                  showName={selectedPerson?.memberCisId === 'EVERYONE'}
                  familyGroups={familyGroups}
                />
              </Suspense>
            </>
          )}
          <Separator />
        </MarginBox>
      </LayoutBand>
      <InfoSheet title={t('story.filters', 'Filters*')} {...infoSheetConfig}>
        <Suspense fallback={<NoticeLoading />}>
          <ActivitygroupFilterSheet
            filteringGroups={filteringGroups}
            selectedIndex={selectedFilteringIndex}
            onSelect={handleFilterSelect}
          />
        </Suspense>
      </InfoSheet>
    </>
  )
}

export default Story
